import { gql, useMutation, useQuery } from '@apollo/client';
import {
	EditCustomerScreen_AddReferral,
	EditCustomerScreen_AddReferralVariables,
	EditCustomerScreen_GetUserReferredByEmail,
	EditCustomerScreen_GetUserReferredByEmailVariables,
	EditCustomerScreen_UpdateReferral,
	EditCustomerScreen_UpdateReferralVariables,
	ReferralSource,
} from '@app/codegen';
import { ReferralComponentLocators } from '@app/e2e/shared/Referral/ReferralComponentLocators';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppState } from '@itrustcapital/ui';
import { Button, Input, Text } from '@ui-kitten/components';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

import { ReferralEmailSearch } from './ReferralEmailSearch';
import { CustomCard } from '../../atoms';

export const EDIT_CUSTOMER_SCREEN_UPDATE_REFERRAL = gql`
	mutation EditCustomerScreen_UpdateReferral($input: UpdateReferralInput!) {
		updateReferral(input: $input) {
			success
			errorMessage
		}
	}
`;

export const EDIT_CUSTOMER_SCREEN_ADD_REFERRAL = gql`
	mutation EditCustomerScreen_AddReferral($input: AddReferralInput!) {
		addReferral(input: $input) {
			success
			errorMessage
		}
	}
`;

export const EDIT_CUSTOMER_SCREEN_GET_USER_REFERRED_BY_EMAIL = gql`
	query EditCustomerScreen_GetUserReferredByEmail($where: UserFilterInput!) {
		users(where: $where, first: 1) {
			nodes {
				referredReferral {
					id
					sentByUserId
					referralSource
					sentByUser {
						email
						referralCode
					}
				}
			}
		}
	}
`;

export interface ReferralProps {
	userId?: number;
}

export const schema = yup
	.object({
		referredEmail: yup.string().default(''),
		selectedReferralId: yup.number().nullable().default(null),
	})
	.defined();

export type ReferralForm = yup.TypeOf<typeof schema> & {
	referredEmail: string;
};

export function Referral(props: ReferralProps) {
	const styles = useCustomStyles();
	const isEditing = useAppState(false);

	const defaultValues = schema.cast({
		referredEmail: '',
		selectedReferralId: null,
	});

	const referralForm = useForm<ReferralForm>({
		mode: 'all',
		resolver: yupResolver(schema),
		defaultValues,
	});

	const [onUpdateReferral, updateReferralMutation] = useMutation<
		EditCustomerScreen_UpdateReferral,
		EditCustomerScreen_UpdateReferralVariables
	>(EDIT_CUSTOMER_SCREEN_UPDATE_REFERRAL, {
		refetchQueries: [EDIT_CUSTOMER_SCREEN_GET_USER_REFERRED_BY_EMAIL],
	});

	const [onAddReferral, addReferralMutation] = useMutation<
		EditCustomerScreen_AddReferral,
		EditCustomerScreen_AddReferralVariables
	>(EDIT_CUSTOMER_SCREEN_ADD_REFERRAL, {
		refetchQueries: [EDIT_CUSTOMER_SCREEN_GET_USER_REFERRED_BY_EMAIL],
	});

	const getExistingReferralDetailsQuery = useQuery<
		EditCustomerScreen_GetUserReferredByEmail,
		EditCustomerScreen_GetUserReferredByEmailVariables
	>(EDIT_CUSTOMER_SCREEN_GET_USER_REFERRED_BY_EMAIL, {
		variables: {
			where: {
				id: {
					eq: props?.userId,
				},
			},
		},
		skip: !props.userId,
	});

	const [existingReferral] = getExistingReferralDetailsQuery?.data?.users?.nodes || [];

	const existingReferredUserDetails = existingReferral?.referredReferral;

	React.useEffect(() => {
		if (!existingReferredUserDetails?.sentByUser) {
			referralForm.reset(defaultValues);

			return;
		}

		prefillForm({
			email: existingReferredUserDetails.sentByUser?.email,
			referralCode: existingReferredUserDetails.sentByUser?.referralCode!,
		});
	}, [existingReferredUserDetails]);

	React.useEffect(() => {
		referralForm.trigger();
	}, [isEditing.get]);

	function prefillForm(sentByUser: { email: string; referralCode?: string }) {
		if (!sentByUser?.email) return;

		const formattedValue = sentByUser.referralCode
			? `${sentByUser.email}-${sentByUser.referralCode.toUpperCase()}`
			: sentByUser.email;

		referralForm.reset({
			referredEmail: formattedValue,
		});
	}

	function showToaster(success: boolean, message?: string) {
		Toast.show({
			type: success ? 'success' : 'error',
			text2: message
				? message
				: `Referral By email ${success ? 'has been updated.' : 'failed to update.'}`,
		});
	}

	async function onSubmit(data: ReferralForm) {
		const { selectedReferralId, referredEmail } = data;

		if (!referredEmail) {
			return showToaster(false, 'Please select an email to be saved.');
		}

		const extractedEmail = referredEmail.split('-')[0];

		if (
			!selectedReferralId &&
			extractedEmail === existingReferredUserDetails?.sentByUser?.email
		) {
			return showToaster(false, 'This referral is already saved.');
		}

		const referredUserId = props?.userId!;
		const sentByUserId = selectedReferralId!;
		const isNewReferral = !existingReferredUserDetails;

		if (isNewReferral) {
			try {
				await onAddReferral({
					variables: {
						input: {
							referralSource: ReferralSource.EMAIL,
							referredUserId,
							sentByUserId,
						},
					},
				});
				isEditing.set(!isEditing.get);

				return showToaster(true);
			} catch {
				return showToaster(false);
			}
		}

		if (sentByUserId === existingReferredUserDetails.sentByUserId) {
			return showToaster(false, 'This referral is already saved.');
		}

		try {
			await onUpdateReferral({
				variables: {
					input: {
						referralId: existingReferredUserDetails.id,
						referralSource: ReferralSource.EMAIL,
						sentByUserId,
					},
				},
			});
			isEditing.set(!isEditing.get);
			showToaster(true);
		} catch (error) {
			showToaster(false);
		}
	}

	return (
		<CustomCard
			header={() => (
				<View style={styles.main.header}>
					<Text category="h6">Referral</Text>
					<View>
						{isEditing.get ? (
							<View style={styles.actions.container}>
								<Button
									appearance="outline"
									style={styles.actions.leftButton}
									onPress={() => {
										isEditing.set(false);
										referralForm.reset();

										setTimeout(() => {
											if (existingReferredUserDetails?.sentByUser?.email) {
												prefillForm({
													email: existingReferredUserDetails.sentByUser
														?.email,
													referralCode:
														existingReferredUserDetails.sentByUser
															?.referralCode!,
												});
											}
										});
									}}
								>
									Cancel
								</Button>
								<Button
									style={styles.actions.save}
									testID={ReferralComponentLocators.referralSaveButton}
									onPress={
										updateReferralMutation.loading ||
										addReferralMutation.loading
											? undefined
											: referralForm.handleSubmit(onSubmit)
									}
								>
									Save
								</Button>
							</View>
						) : (
							<>
								<View style={styles.actions.container}>
									<Button
										testID={ReferralComponentLocators.referralEditButton}
										onPress={() => isEditing.set(!isEditing.get)}
									>
										Edit
									</Button>
								</View>
							</>
						)}
					</View>
				</View>
			)}
			loading={updateReferralMutation.loading || addReferralMutation.loading}
		>
			<View style={styles.main.content}>
				<View style={styles.main.row}>
					<View style={styles.main.col}>
						<View style={styles.main.searchInput}>
							<ReferralEmailSearch disabled={!isEditing.get} form={referralForm} />

							<Controller
								control={referralForm.control}
								name="referredEmail"
								render={(control) => (
									<Input
										disabled
										label="Referred By Email"
										placeholder="Choose an email"
										style={styles.main.selector}
										status={control.fieldState.error && 'danger'}
										testID={ReferralComponentLocators.referralEmailField}
										value={control.field.value!}
									/>
								)}
							/>
						</View>
					</View>
					<View style={styles.main.col} />
				</View>
			</View>
		</CustomCard>
	);
}

function useCustomStyles() {
	return {
		main: StyleSheet.create({
			header: {
				flexDirection: 'row',
				justifyContent: 'space-between',
				alignItems: 'center',
			},
			content: {
				marginBottom: 12,
				flex: 1,
			},
			row: {
				flexDirection: 'row',
				alignItems: 'center',
				marginVertical: 8,
				marginHorizontal: -8,
			},
			col: {
				flex: 1,
				marginHorizontal: 8,
			},
			selector: {
				flex: 1,
			},
			searchInput: {
				flexDirection: 'row-reverse',
				justifyContent: 'space-between',
				alignItems: 'center',
			},
		}),
		actions: StyleSheet.create({
			container: {
				flex: 1,
				flexDirection: 'row',
				justifyContent: 'flex-end',
				alignItems: 'center',
				paddingRight: 6,
			},
			leftButton: {
				marginRight: 12,
			},
			save: {
				minWidth: 70,
			},
		}),
	};
}
